import {
  BoardMeetingState,
  boardMeetingStateTimeline,
  BoardMeeting,
} from "@boark/shared/types/board-meeting";

import { useLocale } from "../stores/locale";
import { formatDateTime } from "../utils/date";

export function BoardMeetingStateTimeline({
  state,
  stateChangedAt,
}: {
  state: BoardMeetingState;
  stateChangedAt: BoardMeeting["stateChangedAt"];
}) {
  const { locale: t } = useLocale();
  const currentIndex = boardMeetingStateTimeline.indexOf(state);

  return (
    <ol
      aria-label={t.boardMeeting.state}
      className="flex items-start list-none m-0 p-0 overflow-x-auto pb-1"
    >
      {boardMeetingStateTimeline.map((stepState, index) => {
        const isCurrent = index === currentIndex;
        const isPast = index < currentIndex;
        const isLast = index === boardMeetingStateTimeline.length - 1;
        const changedAt = stateChangedAt[stepState];

        return (
          <li
            key={stepState}
            className="relative flex flex-1 flex-col items-center min-w-[5.5rem]"
          >
            {index > 0 && (
              <span
                aria-hidden
                className={`absolute top-3 right-1/2 w-full h-0.5 -translate-y-1/2 ${
                  isPast || isCurrent ? "bg-blue-500" : "bg-gray-300"
                }`}
              />
            )}
            {!isLast && (
              <span
                aria-hidden
                className={`absolute top-3 left-1/2 w-full h-0.5 -translate-y-1/2 ${
                  isPast ? "bg-blue-500" : "bg-gray-300"
                }`}
              />
            )}
            <span
              aria-current={isCurrent ? "step" : undefined}
              className={`relative z-10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${
                isCurrent
                  ? "border-blue-600 bg-blue-600 ring-4 ring-blue-100"
                  : isPast
                    ? "border-blue-500 bg-blue-500"
                    : "border-gray-300 bg-white"
              }`}
            />
            <span
              className={`mt-2 w-full px-1 text-center text-xs leading-tight ${
                isCurrent
                  ? "font-semibold text-blue-700"
                  : isPast
                    ? "text-gray-700"
                    : "text-gray-400"
              }`}
            >
              {t.boardMeeting.states[stepState]}
            </span>
            {changedAt && index <= currentIndex && (
              <span className="mt-0.5 text-center text-[0.65rem] text-gray-500">
                {formatDateTime(changedAt, false)}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
